import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import { ActivitiesContentType } from 'global/constants';
import { useGetActivitySections } from 'approot/activity-builder/activity-builder-section.hooks';
import { ComponentActivity } from '../component.constants';
import type { GetLessonTeachingAndLearningSequenceQuery } from './teaching-notes.graphql.generated';
import TeachingNotesDetailRichtextView from './teaching-notes-detail-richtext-view';
import { getActivityPartsSequenceItems } from 'approot/shared/teaching-learning-sequence/logic/get-activity-parts-sequence-items';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  sectionId: string;
  urlSectionId: string;
  contentId: number;
  contentType: ActivitiesContentType;
  classId: number;
  component: ComponentActivity;
  sequences: Pluck<
    GetLessonTeachingAndLearningSequenceQuery,
    'getLessonTeachingAndLearningSequence'
  >[];
  version: string | undefined;
};

const TeachingNotesActivityPartsView = ({
  sectionId,
  urlSectionId,
  contentId,
  contentType,
  classId,
  component,
  sequences,
  version,
}: Props) => {
  const { data: sectionData } = useGetActivitySections({
    contentId,
    contentType,
    classId,
    sectionIds: [sectionId],
    version,
  });

  const activityParts = useMemo(
    () => getActivityPartsSequenceItems(sequences, component.id),
    [sequences, component]
  );

  const subSections = useMemo(
    () => sortBy(sectionData?.getActivitiesSections?.[0].subSections, 'sort'),
    [sectionData?.getActivitiesSections]
  );

  const firstPart = useMemo(
    () => activityParts.find(a => a.sectionId === sectionId),
    [activityParts, sectionId]
  );
  const total = subSections ? subSections.length + 1 : 0;

  return (
    <>
      <TeachingNotesDetailRichtextView
        key={`${firstPart?.id}-${urlSectionId === firstPart?.sectionId}`}
        data={firstPart?.data}
        title={
          total === 1 ? 'Teaching Notes' : `Teaching Notes - Part 1 of ${total}`
        }
        displayExpanded={urlSectionId === firstPart?.sectionId}
      />
      {subSections?.map((subSection, index) => {
        const part = activityParts.find(d => d.sectionId === subSection.id);
        const displayExpanded = urlSectionId === subSection.id;
        return (
          <TeachingNotesDetailRichtextView
            key={`${subSection.id}-${displayExpanded}`}
            title={`Teaching Notes - Part ${index + 2} of ${total}`}
            data={part?.data}
            displayExpanded={displayExpanded}
          />
        );
      })}
    </>
  );
};

export default TeachingNotesActivityPartsView;
